<template>
    <article>
        <p>
            This guide provides a quick overview of launching Coroot Community Edition with default options.
            For more details and customization options check out the Installation section.
        </p>

        <v-tabs v-model="tab" height="40" slider-size="2" show-arrows class="mb-6">
            <v-tab :to="{hash: 'kubernetes'}" tab-value="kubernetes"><v-icon class="mr-1">mdi-kubernetes</v-icon>Kubernetes</v-tab>
            <v-tab :to="{hash: 'docker'}" tab-value="docker"><v-icon class="mr-1">mdi-docker</v-icon>Docker</v-tab>
            <v-tab :to="{hash: 'swarm'}" tab-value="swarm"><v-icon class="mr-1">mdi-docker</v-icon>Docker Swarm</v-tab>
            <v-tab :to="{hash: 'ubuntu'}" tab-value="ubuntu"><v-icon class="mr-1">mdi-ubuntu</v-icon>Ubuntu & Debian</v-tab>
            <v-tab :to="{hash: 'rhel'}" tab-value="rhel"><v-icon class="mr-1">mdi-redhat</v-icon>RHEL & CentOS</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">

            <v-tab-item value="kubernetes" eager transition="none">
                <p>
                    Add the Coroot helm chart repo:
                </p>
                <CodeSnippet>
                    <pre v-hl.bash>
helm repo add coroot https://coroot.github.io/helm-charts
helm repo update coroot
                    </pre>
                </CodeSnippet>

                <p>Install the chart into the <var>coroot</var> namespace:</p>

                <CodeSnippet>
                    <pre v-hl.bash>
helm install --namespace coroot --create-namespace coroot coroot/coroot
                    </pre>
                </CodeSnippet>

                <p>Forward the Coroot port to your machine:</p>

                <CodeSnippet>
                    <pre v-hl.bash>
kubectl port-forward -n coroot service/coroot 8080:8080
                    </pre>
                </CodeSnippet>

                <p>Then, you can access Coroot at http://localhost:8080</p>
            </v-tab-item>

            <v-tab-item value="docker" eager transition="none">
                <p>
                    To deploy Coroot using Docker Compose, run the following command.
                    Before applying it, you can review the configuration file in Coroot's GitHub repository:
                    <a href="https://github.com/coroot/coroot/blob/main/deploy/docker-compose.yaml" target="_blank">docker-compose.yaml</a>
                </p>

                <CodeSnippet>
                    <pre v-hl.bash>
    curl -fsS https://raw.githubusercontent.com/coroot/coroot/main/deploy/docker-compose.yaml | docker compose -f - up -d
                    </pre>
                </CodeSnippet>

                <p>
                    If you installed Coroot on your desktop machine, you can access it at <a href="http://localhost:8080/" target="_blank">http://localhost:8080/</a>.
                    If Coroot is deployed on a remote node, replace &lt;NODE_IP_ADDRESS&gt; with the IP address of the node in the following URL: http://&lt;NODE_IP_ADDRESS&gt;:8080/.
                </p>
            </v-tab-item>

            <v-tab-item value="swarm" eager transition="none">
                <p>
                    Deploy the Coroot stack to your cluster by running the following command on the manager node.
                    Before applying, you can review the configuration file in Coroot's GitHub repository:
                    <a href="https://github.com/coroot/coroot/blob/main/deploy/docker-swarm-stack.yaml" target="_blank">docker-swarm-stack.yaml</a>
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -fsS https://raw.githubusercontent.com/coroot/coroot/main/deploy/docker-swarm-stack.yaml | docker stack deploy -c - coroot
                    </pre>
                </CodeSnippet>

                <p>
                    Since Docker Swarm doesn't support privileged containers, you'll have to manually deploy
                    <var>coroot-node-agent</var> on each cluster node.
                    Just replace <var>&lt;NODE_IP&gt;</var> with any node's IP address in the Docker Swarm cluster.
                </p>

                <CodeSnippet>
                    <pre v-hl.bash>
docker run --detach --name coroot-node-agent \
  --pull=always \
  --privileged --pid host \
  -v /sys/kernel/tracing:/sys/kernel/tracing:rw \
  -v /sys/kernel/debug:/sys/kernel/debug:rw \
  -v /sys/fs/cgroup:/host/sys/fs/cgroup:ro \
  ghcr.io/coroot/coroot-node-agent \
  --cgroupfs-root=/host/sys/fs/cgroup \
  --collector-endpoint=http://&lt;NODE_IP&gt;:8080
                    </pre>
                </CodeSnippet>

                <p>
                    Access Coroot through any node in your Docker Swarm cluster using its published port: http://&lt;node_ip&gt;:8080.
                </p>
            </v-tab-item>

            <v-tab-item value="ubuntu" eager transition="none">
                <p>
                    Coroot requires a Prometheus server with the Remote Write Receiver enabled, along with a ClickHouse server.
                    For detailed steps on installing all the necessary components on an Ubuntu/Debian node, refer to the
                    <router-link :to="{name: 'docs', params: {chapter: 'coroot', group: 'installation', item: 'ubuntu'}}">full instructions</router-link>.
                </p>

                <p>To install Coroot, run the following command:</p>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -sfL https://raw.githubusercontent.com/coroot/coroot/main/deploy/install.sh | \
    BOOTSTRAP_PROMETHEUS_URL="http://&lt;PROMETHEUS_IP&gt;:9090" \
    BOOTSTRAP_REFRESH_INTERVAL=15s \
    BOOTSTRAP_CLICKHOUSE_ADDRESS=&lt;CLICKHOUSE_IP&gt;:9000 \
    sh -
                    </pre>
                </CodeSnippet>

                <p>Install coroot-node-agent to every node within your infrastructure:</p>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -sfL https://raw.githubusercontent.com/coroot/coroot-node-agent/main/install.sh | \
    COLLECTOR_ENDPOINT=http://&lt;COROOT_NODE_IP&gt;:8080 \
    SCRAPE_INTERVAL=15s \
    sh -
                    </pre>
                </CodeSnippet>

                <p>
                    Access Coroot at: http://&lt;COROOT_NODE_IP&gt;:8080.
                </p>
            </v-tab-item>

            <v-tab-item value="rhel" eager transition="none">
                <p>
                    Coroot requires a Prometheus server with the Remote Write Receiver enabled, along with a ClickHouse server.
                    For detailed steps on installing all the necessary components on an Ubuntu/Debian node, refer to the
                    <router-link :to="{name: 'docs', params: {chapter: 'coroot', group: 'installation', item: 'rhel'}}">full instructions</router-link>.
                </p>

                <p>To install Coroot, run the following command:</p>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -sfL https://raw.githubusercontent.com/coroot/coroot/main/deploy/install.sh | \
    BOOTSTRAP_PROMETHEUS_URL="http://&lt;PROMETHEUS_IP&gt;:9090" \
    BOOTSTRAP_REFRESH_INTERVAL=15s \
    BOOTSTRAP_CLICKHOUSE_ADDRESS=&lt;CLICKHOUSE_IP&gt;:9000 \
    sh -
                    </pre>
                </CodeSnippet>

                <p>Install coroot-node-agent to every node within your infrastructure:</p>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -sfL https://raw.githubusercontent.com/coroot/coroot-node-agent/main/install.sh | \
    COLLECTOR_ENDPOINT=http://&lt;COROOT_NODE_IP&gt;:8080 \
    SCRAPE_INTERVAL=15s \
    sh -
                    </pre>
                </CodeSnippet>

                <p>
                    Access Coroot at: http://&lt;COROOT_NODE_IP&gt;:8080.
                </p>
            </v-tab-item>
        </v-tabs-items>

    </article>
</template>

<script>

export default {
    data() {
        return {
            tab: null,
        };
    },
}
</script>

<style scoped>
</style>
